import { useCallback, useEffect, useRef, useState } from "react";
import { IconArrowDown, IconLoading } from "@icons/index";
import Image from "@components/Image";
import { CommonButton } from "@components/CommonButton";
import type { UserAssetV2Record } from "@apis/http/api";
import { getAssetCoverUrl } from "@pages/library/hooks/useGeneratedVideoAssets";
import { RotatingGradientBg } from "@pages/library/UICardWrap/UIVideoGenerateCard";

interface CardListSidebarProps {
  records: UserAssetV2Record[];
  activeJobId: string | number | undefined;
  onSelect: (jobId: string | number | undefined) => void;
}

export const CardListSidebar = ({ records, activeJobId, onSelect }: CardListSidebarProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeJobId]);

  const scrollPage = useCallback((direction: "up" | "down") => {
    const el = listRef.current;
    if (!el) return;
    const distance = el.clientHeight;
    el.scrollBy({ top: direction === "up" ? -distance : distance, behavior: "smooth" });
  }, []);

  return (
    <div className="flex flex-col h-full bg-kds-bg">
      {/* Scroll Up */}
      <div className="shrink-0 flex items-center justify-center py-[16px]">
        <CommonButton
          className="w-[42px] h-[24px]! p-0!"
          disabled={!canScrollUp}
          onClick={() => canScrollUp && scrollPage("up")}
        >
          <IconArrowDown className="w-[16px] h-[16px] rotate-180" />
        </CommonButton>
      </div>

      {/* Card List */}
      <div
        className="flex-1 min-h-0 flex items-center flex-col gap-[16px] py-[8px] overflow-y-auto overflow-x-hidden scrollbar-none"
        ref={listRef}
      >
        {records.map((record) => {
          const coverImageUrl = getAssetCoverUrl(record);
          const status = record?.jobItem?.status;
          const jobId = record?.jobItem?.jobId;
          const isActive = jobId === activeJobId;
          const isProcessing = status === "processing" || status === "queued";

          return (
            <div
              key={record.id}
              ref={isActive ? activeRef : null}
              className="w-[62px] h-[62px] rounded-[4px] relative shrink-0 cursor-pointer"
              onClick={() => onSelect(jobId)}
            >
              {isActive && (
                <div className="absolute -top-[6px] -left-[6px] -right-[6px] -bottom-[6px] border-kds-accent border-2 rounded-[8px]" />
              )}
              {isProcessing ? (
                <div className="w-full h-full overflow-hidden relative generate-card-item flex flex-col items-center justify-center rounded-[8px]">
                  <RotatingGradientBg showDot={false} />
                  <IconLoading className="w-[16px] h-[16px] animate-spin opacity-50" />
                </div>
              ) : (
                <Image
                  className="w-full h-full shrink-0 overflow-hidden border-none! bg-contain! bg-kds-bg-subtle!"
                  url={coverImageUrl}
                  type="video"
                  emptyClassName="w-[24px] h-[24px]"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Scroll Down */}
      <div className="shrink-0 flex items-center justify-center py-[16px]">
        <CommonButton
          className="w-[42px] h-[24px]! p-0!"
          disabled={!canScrollDown}
          onClick={() => canScrollDown && scrollPage("down")}
        >
          <IconArrowDown className="w-[16px] h-[16px]" />
        </CommonButton>
      </div>
    </div>
  );
};
